import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface EditableSessionTitleProps {
  title: string;
  onSave: (newTitle: string) => Promise<void> | void;
  isMobile?: boolean;
}

export const EditableSessionTitle: React.FC<EditableSessionTitleProps> = ({
  title,
  onSave,
  isMobile = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleStartEditing = useCallback(() => {
    setInputValue(title || '');
    setIsEditing(true);
  }, [title]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setInputValue('');
  }, []);

  const handleCommit = useCallback(async () => {
    const next = (inputValue || '').trim();
    if (!next) {
      setIsEditing(false);
      return;
    }
    
    // If the title hasn't changed, just exit edit mode without saving
    // This prevents unnecessary API calls and state updates that could cause UI glitches
    if (next === (title || '').trim()) {
      setIsEditing(false);
      return;
    }
    
    try {
      setIsSaving(true);
      await onSave(next);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [inputValue, onSave, title]);

  // Scroll input into view and select all text when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        const input = inputRef.current;
        if (input) {
          // Select all text for easy deletion
          input.select();
          // Scroll the input into view with smooth behavior and align to start
          input.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start'
          });
          // Also ensure the container scrolls if needed
          if (containerRef.current) {
            containerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start'
            });
          }
        }
      }, 50);
    }
  }, [isEditing]);

  const textStyles = useMemo(
    () =>
      isMobile
        ? {
            fontSize: '1.125rem',
            fontWeight: 600 as const,
            lineHeight: 1.3
          }
        : {
            fontSize: '14px',
            fontWeight: 500 as const,
            lineHeight: 1.4
          },
    [isMobile]
  );

  if (isEditing) {
    return (
      <Box 
        ref={containerRef}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          width: isMobile ? '100%' : 'auto',
          minWidth: isMobile ? 0 : 'auto',
          maxWidth: isMobile ? '100%' : 'none',
          overflow: 'visible',
          position: 'relative',
          zIndex: 1
        }}
      >
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCommit();
            if (e.key === 'Escape') handleCancel();
          }}
          onBlur={handleCommit}
          placeholder="Enter session title..."
          autoFocus
          variant="standard"
          disabled={isSaving}
          fullWidth={isMobile}
          sx={{
            minWidth: isMobile ? 0 : 'auto',
            width: isMobile ? '100%' : 'auto',
            '& .MuiInput-input': {
              ...textStyles,
              padding: 0,
              minWidth: isMobile ? 0 : `${Math.max((inputValue || title || '').length * 0.6, 20)}em`,
              width: isMobile ? '100%' : 'auto',
              whiteSpace: 'nowrap',
              overflow: 'visible'
            },
            '& .MuiInput-underline:before': { display: 'none' },
            '& .MuiInput-underline:after': { display: 'none' },
            '& .MuiInput-root': {
              minWidth: isMobile ? 0 : 'auto',
              width: isMobile ? '100%' : 'auto'
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      onClick={handleStartEditing}
      sx={{ 
        cursor: 'pointer', 
        transition: 'opacity 0.15s ease',
        '&:hover': { opacity: 0.7 },
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
   >
      <Typography 
        sx={{
          ...textStyles,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          textDecoration: 'none'
        }} 
        variant={isMobile ? 'subtitle1' : 'h6'}
      >
        {title || 'Untitled Session'}
      </Typography>
    </Box>
  );
};

export default EditableSessionTitle;




